import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import useAutoRefresh from '@/hooks/useAutoRefresh';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '@/components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  BookOpen, Plus, Search, Edit, Trash2, Save, X, RefreshCw,
  HelpCircle, CheckCircle, Filter, Download, Upload, Languages,
  BarChart3, Settings, ChevronRight, AlertCircle, Eye, FileSpreadsheet,
  FileText, CloudUpload, FileDown, Sparkles, Wand2, Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Professional courses are now loaded dynamically from /api/courses (Single Source of Truth).
// No more hardcoded list — newly added courses in /admin/courses appear here automatically.

const DIFFICULTY_LEVELS = ['easy', 'medium', 'hard'];
const QUESTION_TYPES = [
  { value: 'technical', label: 'Technical' },
  { value: 'soft_skill', label: 'Soft Skill - General' },
  { value: 'personality', label: 'Personality Development' },
  { value: 'customer_relations', label: 'Customer Relationship' },
  { value: 'marketing', label: 'Marketing & Communication' },
];

const AdminQuestionBankPage = () => {
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);
  
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterModule, setFilterModule] = useState('all');
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [modules, setModules] = useState([]);
  // Dynamic professional courses (sourced from /api/courses — same as /admin/courses)
  const [courses, setCourses] = useState([]);
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showBulkUploadDialog, setShowBulkUploadDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [previewQuestion, setPreviewQuestion] = useState(null);
  const [previewLang, setPreviewLang] = useState('en');
  
  // Bulk upload states
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCourseId, setUploadCourseId] = useState('');
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [useAiTranslation, setUseAiTranslation] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [upsertMode, setUpsertMode] = useState(false);
  
  // Course subjects for subject_code dropdown
  const [courseSubjects, setCourseSubjects] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    course_id: '',
    module: '',
    subject_code: '',
    subject_title: '',
    subject_type: '',
    difficulty: 'medium',
    question_type: 'technical',
    question_en: '',
    option_a_en: '',
    option_b_en: '',
    option_c_en: '',
    option_d_en: '',
    question_hi: '',
    option_a_hi: '',
    option_b_hi: '',
    option_c_hi: '',
    option_d_hi: '',
    correct_answer: 'A',
    marks: 1.0
  });

  const getHeaders = () => ({
    'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  // Fetch stats on mount
  useEffect(() => {
    fetchStats();
    fetchCoursesList();
  }, []);

  // Auto-refresh on tab focus
  useAutoRefresh(() => {
    fetchStats();
    fetchCoursesList();
    if (selectedCourse) fetchCourseQuestions(selectedCourse);
  });

  // Load all professional courses dynamically from public courses endpoint
  const fetchCoursesList = async () => {
    try {
      const res = await axios.get(`${API}/courses`);
      const all = Array.isArray(res.data) ? res.data : [];
      const pro = all
        .filter((c) => c.category === 'professional' && c.is_active !== false)
        .map((c) => ({ id: c.id, name: c.name }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setCourses(pro);
    } catch (e) {
      console.error('Failed to load courses', e);
    }
  };

  // Fetch questions when course changes
  useEffect(() => {
    if (selectedCourse) {
      fetchCourseQuestions(selectedCourse);
      fetchCourseSubjects(selectedCourse);
    }
  }, [selectedCourse]);

  // Fetch subjects for a course
  const fetchCourseSubjects = async (courseId) => {
    try {
      const res = await axios.get(`${API}/admin/courses/${courseId}/subjects`, { headers: getHeaders() });
      setCourseSubjects(res.data.subjects || []);
    } catch {
      setCourseSubjects([]);
    }
  };

  // Filter questions
  useEffect(() => {
    let filtered = [...questions];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(q => 
        q.question_en?.toLowerCase().includes(term) ||
        q.question_hi?.toLowerCase().includes(term) ||
        q.module?.toLowerCase().includes(term)
      );
    }
    
    if (filterModule && filterModule !== 'all') {
      filtered = filtered.filter(q => q.module === filterModule);
    }
    
    if (filterDifficulty && filterDifficulty !== 'all') {
      filtered = filtered.filter(q => q.difficulty === filterDifficulty);
    }
    
    setFilteredQuestions(filtered);
  }, [questions, searchTerm, filterModule, filterDifficulty]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/question-bank/stats`, { headers: getHeaders() });
      setStats(res.data);
    } catch (error) {
      toast.error('Failed to fetch stats');
    }
  };

  const fetchCourseQuestions = async (courseId) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/question-bank/questions/${courseId}`,
        { headers: getHeaders() }
      );
      setQuestions(res.data.questions || []);
      setModules(Object.keys(res.data.by_module || {}));
    } catch (error) {
      toast.error('Failed to fetch questions');
      setQuestions([]);
    }
    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      course_id: selectedCourse || '',
      module: '',
      subject_code: '',
      subject_title: '',
      subject_type: '',
      difficulty: 'medium',
      question_type: 'technical',
      question_en: '',
      option_a_en: '',
      option_b_en: '',
      option_c_en: '',
      option_d_en: '',
      question_hi: '',
      option_a_hi: '',
      option_b_hi: '',
      option_c_hi: '',
      option_d_hi: '',
      correct_answer: 'A',
      marks: 1.0
    });
  };

  const handleAddQuestion = async () => {
    if (!formData.course_id || !formData.question_en || !formData.question_hi) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(
        `${API}/question-bank/questions`,
        formData,
        { headers: getHeaders() }
      );
      toast.success('Question added successfully');
      setShowAddDialog(false);
      resetForm();
      fetchCourseQuestions(selectedCourse);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add question');
    }
    setLoading(false);
  };

  const handleEditQuestion = async () => {
    if (!editingQuestion) return;
    
    setLoading(true);
    try {
      await axios.put(
        `${API}/question-bank/questions/${editingQuestion.id}`,
        formData,
        { headers: getHeaders() }
      );
      toast.success('Question updated successfully');
      setShowEditDialog(false);
      setEditingQuestion(null);
      resetForm();
      fetchCourseQuestions(selectedCourse);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update question');
    }
    setLoading(false);
  };

  const handleDeleteQuestion = async () => {
    if (!editingQuestion) return;
    
    setLoading(true);
    try {
      await axios.delete(
        `${API}/question-bank/questions/${editingQuestion.id}`,
        { headers: getHeaders() }
      );
      toast.success('Question deleted');
      setShowDeleteDialog(false);
      setEditingQuestion(null);
      fetchCourseQuestions(selectedCourse);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete question');
    }
    setLoading(false);
  };

  const openEditDialog = (question) => {
    setEditingQuestion(question);
    setFormData({
      course_id: question.course_id,
      module: question.module,
      subject_code: question.subject_code || '',
      subject_title: question.subject_title || '',
      subject_type: question.subject_type || '',
      difficulty: question.difficulty,
      question_type: question.question_type,
      question_en: question.question_en,
      option_a_en: question.option_a_en,
      option_b_en: question.option_b_en,
      option_c_en: question.option_c_en,
      option_d_en: question.option_d_en,
      question_hi: question.question_hi,
      option_a_hi: question.option_a_hi,
      option_b_hi: question.option_b_hi,
      option_c_hi: question.option_c_hi,
      option_d_hi: question.option_d_hi,
      correct_answer: question.correct_answer,
      marks: question.marks
    });
    setShowEditDialog(true);
  };

  const openPreviewDialog = (question) => {
    setPreviewQuestion(question);
    setPreviewLang('en');
    setShowPreviewDialog(true);
  };

  // Bulk Upload Functions
  // Export questions for bulk-edit (with optional course filter)
  const exportQuestionBank = async (format = 'csv') => {
    try {
      const params = new URLSearchParams();
      if (uploadCourseId && uploadCourseId !== '__from_file__') {
        params.set('course_id', uploadCourseId);
      }
      params.set('format', format);
      const res = await axios.get(`${API}/question-bank/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'xlsx' ? 'question_bank.xlsx' : 'question_bank.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Question bank exported');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  const handleBulkUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }
    
    setUploading(true);
    setUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      // Only pass course_id if a specific course is selected from dropdown (not __from_file__ or empty)
      const courseIdToSend = uploadCourseId && uploadCourseId !== '__from_file__' ? uploadCourseId : '';
      
      // Build endpoint URL - only include course_id param if user explicitly selected a course
      let endpoint;
      if (useAiTranslation) {
        endpoint = courseIdToSend 
          ? `${API}/question-bank/bulk-translate?course_id=${courseIdToSend}`
          : `${API}/question-bank/bulk-translate`;
      } else {
        const params = new URLSearchParams();
        if (courseIdToSend) params.set('course_id', courseIdToSend);
        if (upsertMode) params.set('mode', 'upsert');
        endpoint = `${API}/question-bank/bulk-upload${params.toString() ? `?${params.toString()}` : ''}`;
      }
      
      const res = await axios.post(
        endpoint,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setUploadResult(res.data);
      
      const totalChanges = (res.data.added || 0) + (res.data.updated || 0);
      if (totalChanges > 0) {
        let msg;
        if (useAiTranslation) {
          msg = `${res.data.added} questions uploaded with AI translation!`;
        } else if (upsertMode) {
          msg = `${res.data.added || 0} added, ${res.data.updated || 0} updated`;
        } else {
          msg = `${res.data.added} questions uploaded successfully!`;
        }
        toast.success(msg);
        fetchStats();
        if (selectedCourse) {
          fetchCourseQuestions(selectedCourse);
        }
      }
      
      if (res.data.errors_count > 0) {
        toast.warning(`${res.data.errors_count} rows had errors`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
      setUploadResult({ error: error.response?.data?.detail || 'Upload failed' });
    }
    setUploading(false);
  };

  const downloadTemplate = async (format = 'csv') => {
    try {
      const response = await axios.get(
        `${API}/question-bank/download-template?format=${format}`,
        {
          headers: getHeaders(),
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'xlsx' ? 'question_template.xlsx' : 'question_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const resetBulkUpload = () => {
    setUploadFile(null);
    setUploadCourseId('');
    setUploadResult(null);
    setUseAiTranslation(false);
  };

  // AI Auto-Translate function for single question form
  const handleAutoTranslate = async () => {
    if (!formData.question_en || !formData.option_a_en || !formData.option_b_en || 
        !formData.option_c_en || !formData.option_d_en) {
      toast.error('Please fill all English fields first');
      return;
    }
    
    setTranslating(true);
    try {
      const res = await axios.post(
        `${API}/question-bank/translate-to-hindi`,
        {
          question_en: formData.question_en,
          option_a_en: formData.option_a_en,
          option_b_en: formData.option_b_en,
          option_c_en: formData.option_c_en,
          option_d_en: formData.option_d_en
        },
        { headers: getHeaders() }
      );
      
      if (res.data.success) {
        setFormData(prev => ({
          ...prev,
          question_hi: res.data.translations.question_hi,
          option_a_hi: res.data.translations.option_a_hi,
          option_b_hi: res.data.translations.option_b_hi,
          option_c_hi: res.data.translations.option_c_hi,
          option_d_hi: res.data.translations.option_d_hi
        }));
        toast.success('Hindi translation generated successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Translation failed');
    }
    setTranslating(false);
  };

  // Render Overview Tab
  const renderOverview = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
          <BookOpen className="h-8 w-8 text-blue-500 mb-2" />
          <p className="text-2xl font-bold text-blue-700">
            {stats.reduce((acc, s) => acc + s.total_questions, 0)}
          </p>
          <p className="text-sm text-blue-600">Total Questions</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 border border-green-100">
          <CheckCircle className="h-8 w-8 text-green-500 mb-2" />
          <p className="text-2xl font-bold text-green-700">
            {stats.filter(s => s.ready_for_exam).length}
          </p>
          <p className="text-sm text-green-600">Courses Ready</p>
        </div>
        <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
          <HelpCircle className="h-8 w-8 text-purple-500 mb-2" />
          <p className="text-2xl font-bold text-purple-700">
            {stats.reduce((acc, s) => acc + s.technical_questions, 0)}
          </p>
          <p className="text-sm text-purple-600">Technical Questions</p>
        </div>
        <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
          <Languages className="h-8 w-8 text-orange-500 mb-2" />
          <p className="text-2xl font-bold text-orange-700">
            {stats.reduce((acc, s) => acc + s.soft_skill_questions, 0)}
          </p>
          <p className="text-sm text-orange-600">Soft Skill Questions</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">Course-wise Question Distribution</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-4 font-medium text-slate-600">Course</th>
                <th className="text-center p-4 font-medium text-slate-600">Total</th>
                <th className="text-center p-4 font-medium text-slate-600">Technical</th>
                <th className="text-center p-4 font-medium text-slate-600">Soft Skills</th>
                <th className="text-center p-4 font-medium text-slate-600">Status</th>
                <th className="text-center p-4 font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {stats.map(stat => (
                <tr key={stat.course_id} className="hover:bg-slate-50">
                  <td className="p-4">
                    <div className="font-medium text-slate-800">{stat.course_name}</div>
                    <div className="text-xs text-slate-500">{stat.course_id}</div>
                  </td>
                  <td className="p-4 text-center">
                    <span className="font-bold text-slate-800">{stat.total_questions}</span>
                    <span className="text-slate-400">/200</span>
                  </td>
                  <td className="p-4 text-center text-slate-600">{stat.technical_questions}</td>
                  <td className="p-4 text-center text-slate-600">{stat.soft_skill_questions}</td>
                  <td className="p-4 text-center">
                    {stat.ready_for_exam ? (
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        Ready
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                        Need {45 - stat.total_questions} more
                      </span>
                    )}
                  </td>
                  <td className="p-4 text-center">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedCourse(stat.course_id);
                        setActiveTab('manage');
                      }}
                    >
                      Manage <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Render Manage Tab
  const renderManage = () => (
    <div className="space-y-6">
      {/* Course Selector */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <Label className="mb-2 block">Select Course</Label>
            <Select value={selectedCourse} onValueChange={setSelectedCourse}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a course..." />
              </SelectTrigger>
              <SelectContent>
                {courses.map(course => (
                  <SelectItem key={course.id} value={course.id}>
                    {course.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {selectedCourse && (
            <>
              <div className="flex-1 min-w-[200px]">
                <Label className="mb-2 block">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search questions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div className="min-w-[150px]">
                <Label className="mb-2 block">Module</Label>
                <Select value={filterModule} onValueChange={setFilterModule}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Modules" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Modules</SelectItem>
                    {modules.map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="min-w-[120px]">
                <Label className="mb-2 block">Difficulty</Label>
                <Select value={filterDifficulty} onValueChange={setFilterDifficulty}>
                  <SelectTrigger>
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="pt-6">
                <Button onClick={() => {
                  resetForm();
                  setFormData(prev => ({ ...prev, course_id: selectedCourse }));
                  setShowAddDialog(true);
                }}>
                  <Plus className="h-4 w-4 mr-2" /> Add Question
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Questions List */}
      {selectedCourse && (
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-800">
                {courses.find(c => c.id === selectedCourse)?.name} Questions
              </h3>
              <p className="text-sm text-slate-500">
                Showing {filteredQuestions.length} of {questions.length} questions
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => fetchCourseQuestions(selectedCourse)}>
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mx-auto" />
              <p className="text-slate-500 mt-2">Loading questions...</p>
            </div>
          ) : filteredQuestions.length === 0 ? (
            <div className="p-12 text-center">
              <HelpCircle className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No questions found</p>
              <Button className="mt-4" onClick={() => {
                resetForm();
                setFormData(prev => ({ ...prev, course_id: selectedCourse }));
                setShowAddDialog(true);
              }}>
                <Plus className="h-4 w-4 mr-2" /> Add First Question
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
              {filteredQuestions.map((q, idx) => (
                <div key={q.id} className="p-4 hover:bg-slate-50">
                  <div className="flex items-start gap-4">
                    <span className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 mb-1 line-clamp-2">
                        {q.question_en}
                      </p>
                      <p className="text-sm text-slate-500 mb-2 line-clamp-1">
                        {q.question_hi}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                          {q.module}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          q.difficulty === 'easy' ? 'bg-green-100 text-green-600' :
                          q.difficulty === 'hard' ? 'bg-red-100 text-red-600' :
                          'bg-amber-100 text-amber-600'
                        }`}>
                          {q.difficulty}
                        </span>
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded text-xs">
                          Answer: {q.correct_answer}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => openPreviewDialog(q)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEditDialog(q)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => {
                        setEditingQuestion(q);
                        setShowDeleteDialog(true);
                      }}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Render Bulk Upload Tab
  const renderBulkUpload = () => (
    <div className="space-y-6">
      {/* AI Translation Feature Card */}
      <div className="bg-gradient-to-r from-purple-50 via-pink-50 to-purple-50 rounded-xl border border-purple-200 p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-purple-100 rounded-lg">
            <Sparkles className="h-8 w-8 text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-800 mb-2 flex items-center gap-2">
              AI-Powered Auto-Translation
              <span className="px-2 py-0.5 bg-purple-500 text-white text-xs rounded-full">NEW</span>
            </h3>
            <p className="text-slate-600 text-sm mb-4">
              Upload questions in <strong>English only</strong> and let AI automatically translate to Hindi! 
              Save 50% of your content creation time. Powered by GPT-4o.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setUseAiTranslation(!useAiTranslation)}
                className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
                  useAiTranslation ? 'bg-purple-500' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                    useAiTranslation ? 'translate-x-8' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`font-medium ${useAiTranslation ? 'text-purple-700' : 'text-slate-500'}`}>
                {useAiTranslation ? 'AI Translation ON' : 'AI Translation OFF'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions Card */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100 p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <FileSpreadsheet className="h-8 w-8 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-800 mb-2">
              Bulk Upload Questions via CSV/Excel
            </h3>
            <p className="text-slate-600 text-sm mb-4">
              {useAiTranslation 
                ? "Upload English questions only - Hindi will be auto-generated by AI!"
                : "Upload questions with both English & Hindi content."
              }
            </p>
            <div className="flex flex-wrap gap-3">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => downloadTemplate('csv')}
                className="bg-white"
              >
                <FileDown className="h-4 w-4 mr-2" /> Download CSV Template
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => downloadTemplate('xlsx')}
                className="bg-white"
              >
                <FileDown className="h-4 w-4 mr-2" /> Download Excel Template
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportQuestionBank('csv')}
                className="bg-white border-purple-200 text-purple-700 hover:bg-purple-50"
                data-testid="export-questions-csv"
                title="Export current question bank for bulk-edit (filtered by Target Course if selected)"
              >
                <Download className="h-4 w-4 mr-2" /> Export {uploadCourseId && uploadCourseId !== '__from_file__' ? 'Selected Course' : 'All'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Upload className="h-5 w-5 text-blue-500" /> Upload Questions File
        </h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Left: File Selection */}
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Target Course</Label>
              <Select value={uploadCourseId} onValueChange={setUploadCourseId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a course (overrides file)..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__from_file__">Use course_id from file</SelectItem>
                  {courses.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                <strong>Recommended:</strong> Select a specific course — saari rows us course ke under save ho jayengi (file ka <code>course_id</code> column ignore hoga). Agar file me sahi <code>course_id</code> values pehle se hain tabhi "Use course_id from file" choose karein.
              </p>
            </div>

            <div>
              <Label className="mb-2 block">Select File</Label>
              <div 
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  uploadFile 
                    ? 'border-green-300 bg-green-50' 
                    : 'border-slate-300 hover:border-blue-400 hover:bg-blue-50'
                }`}
              >
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => {
                    setUploadFile(e.target.files[0]);
                    setUploadResult(null);
                  }}
                  className="hidden"
                  id="bulk-upload-input"
                />
                <label htmlFor="bulk-upload-input" className="cursor-pointer">
                  {uploadFile ? (
                    <div className="space-y-2">
                      <CheckCircle className="h-10 w-10 text-green-500 mx-auto" />
                      <p className="font-medium text-green-700">{uploadFile.name}</p>
                      <p className="text-xs text-green-600">
                        {(uploadFile.size / 1024).toFixed(1)} KB
                      </p>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          setUploadFile(null);
                          setUploadResult(null);
                        }}
                      >
                        Change File
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <CloudUpload className="h-10 w-10 text-slate-400 mx-auto" />
                      <p className="font-medium text-slate-600">Click to select file</p>
                      <p className="text-xs text-slate-400">CSV or Excel (.xlsx)</p>
                    </div>
                  )}
                </label>
              </div>
            </div>

            {/* Upsert toggle — bulk-edit mode */}
            <label className="flex items-start gap-2 p-3 rounded-lg border border-purple-200 bg-purple-50 cursor-pointer">
              <input
                type="checkbox"
                checked={upsertMode}
                onChange={(e) => setUpsertMode(e.target.checked)}
                disabled={useAiTranslation}
                className="mt-0.5 w-4 h-4 accent-purple-600"
                data-testid="qb-upsert-mode-toggle"
              />
              <div className="text-sm">
                <p className="font-semibold text-purple-900">Update existing questions (instead of duplicate)</p>
                <p className="text-purple-700 text-xs mt-0.5">
                  Match by <code>id</code> column. Use this with the <strong>Export</strong> file
                  to bulk-edit translations, marks, difficulty, etc. Disabled in AI Translation mode.
                </p>
              </div>
            </label>

            <Button 
              onClick={handleBulkUpload} 
              disabled={!uploadFile || uploading}
              className={`w-full ${useAiTranslation ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600' : ''}`}
              size="lg"
            >
              {uploading ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" /> 
                  {useAiTranslation ? 'Translating & Uploading...' : 'Uploading...'}
                </>
              ) : (
                <>
                  {useAiTranslation ? (
                    <>
                      <Wand2 className="h-4 w-4 mr-2" /> Upload with AI Translation
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" /> Upload Questions
                    </>
                  )}
                </>
              )}
            </Button>
            
            {useAiTranslation && (
              <p className="text-xs text-purple-600 text-center">
                AI will translate English questions to Hindi automatically
              </p>
            )}
          </div>

          {/* Right: Results */}
          <div className="space-y-4">
            <Label className="mb-2 block">Upload Results</Label>
            {uploadResult ? (
              <div className="space-y-4">
                {uploadResult.error ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-red-600 font-medium mb-2">
                      <AlertCircle className="h-5 w-5" /> Upload Failed
                    </div>
                    <p className="text-sm text-red-600">{uploadResult.error}</p>
                  </div>
                ) : (
                  <>
                    {/* Stats */}
                    <div className="grid grid-cols-4 gap-3">
                      <div className="bg-slate-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-slate-800">{uploadResult.total_rows}</p>
                        <p className="text-xs text-slate-500">Total Rows</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-green-600">{uploadResult.added || 0}</p>
                        <p className="text-xs text-green-600">Added</p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-purple-600">{uploadResult.updated || 0}</p>
                        <p className="text-xs text-purple-600">Updated</p>
                      </div>
                      <div className="bg-red-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-red-600">{uploadResult.errors_count}</p>
                        <p className="text-xs text-red-600">Errors</p>
                      </div>
                    </div>

                    {/* Success Message */}
                    {((uploadResult.added || 0) + (uploadResult.updated || 0)) > 0 && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-green-600 font-medium">
                          <CheckCircle className="h-5 w-5" /> 
                          {uploadResult.added || 0} added{uploadResult.updated ? `, ${uploadResult.updated} updated` : ''} successfully!
                        </div>
                      </div>
                    )}

                    {/* Errors List */}
                    {uploadResult.errors && uploadResult.errors.length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-amber-700 font-medium mb-2">
                          <AlertCircle className="h-5 w-5" /> Errors ({uploadResult.errors_count})
                        </div>
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {uploadResult.errors.map((err, idx) => (
                            <p key={idx} className="text-xs text-amber-600 font-mono">
                              {err}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}

                    <Button 
                      variant="outline" 
                      onClick={resetBulkUpload}
                      className="w-full"
                    >
                      Upload Another File
                    </Button>
                  </>
                )}
              </div>
            ) : (
              <div className="bg-slate-50 rounded-lg p-8 text-center">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Upload a file to see results</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Column Reference */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5 text-purple-500" /> Required Columns
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-3 font-medium text-slate-600">Column Name</th>
                <th className="text-left p-3 font-medium text-slate-600">Required</th>
                <th className="text-left p-3 font-medium text-slate-600">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr><td className="p-3 font-mono text-blue-600">course_id</td><td className="p-3">Optional*</td><td className="p-3 text-slate-600">Course slug (e.g., autocad, tally-gst)</td></tr>
              <tr><td className="p-3 font-mono text-blue-600">module</td><td className="p-3">Yes</td><td className="p-3 text-slate-600">Module/Topic name</td></tr>
              <tr><td className="p-3 font-mono text-blue-600">difficulty</td><td className="p-3">Yes</td><td className="p-3 text-slate-600">easy, medium, or hard</td></tr>
              <tr><td className="p-3 font-mono text-blue-600">question_type</td><td className="p-3">Yes</td><td className="p-3 text-slate-600">technical, soft_skill, personality, customer_relations, marketing</td></tr>
              <tr><td className="p-3 font-mono text-blue-600">question_en</td><td className="p-3">Yes</td><td className="p-3 text-slate-600">Question in English</td></tr>
              <tr><td className="p-3 font-mono text-blue-600">option_a_en ... option_d_en</td><td className="p-3">Yes</td><td className="p-3 text-slate-600">Options A-D in English</td></tr>
              <tr><td className="p-3 font-mono text-blue-600">question_hi</td><td className="p-3">Yes</td><td className="p-3 text-slate-600">Question in Hindi</td></tr>
              <tr><td className="p-3 font-mono text-blue-600">option_a_hi ... option_d_hi</td><td className="p-3">Yes</td><td className="p-3 text-slate-600">Options A-D in Hindi</td></tr>
              <tr><td className="p-3 font-mono text-blue-600">correct_answer</td><td className="p-3">Yes</td><td className="p-3 text-slate-600">A, B, C, or D</td></tr>
              <tr><td className="p-3 font-mono text-blue-600">marks</td><td className="p-3">No</td><td className="p-3 text-slate-600">Marks per question (default: 1)</td></tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          * course_id column is optional if you select a default course above
        </p>
      </div>
    </div>
  );

  // Question Form Component
  const QuestionForm = ({ isEdit = false }) => (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Course *</Label>
          <Select 
            value={formData.course_id} 
            onValueChange={(v) => {
              setFormData({...formData, course_id: v, subject_code: '', subject_title: '', subject_type: ''});
              // Fetch subjects for the selected course
              fetchCourseSubjects(v);
            }}
            disabled={isEdit}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select course" />
            </SelectTrigger>
            <SelectContent>
              {courses.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Module/Topic *</Label>
          <Input
            value={formData.module}
            onChange={(e) => setFormData({...formData, module: e.target.value})}
            placeholder="e.g., AutoCAD Basics"
          />
        </div>
      </div>

      {/* Subject Code, Title & Type - for marksheet mapping */}
      <div className="grid grid-cols-3 gap-3 bg-blue-50 p-3 rounded-lg border border-blue-200">
        <div>
          <Label className="text-xs text-blue-700">Subject Code (NOS)</Label>
          {courseSubjects.length > 0 ? (
            <Select 
              value={formData.subject_code}
              onValueChange={(v) => {
                const subj = courseSubjects.find(s => s.subject_code === v);
                setFormData({
                  ...formData, 
                  subject_code: v,
                  subject_title: subj?.subject_title || '',
                  subject_type: subj?.subject_type || ''
                });
              }}
            >
              <SelectTrigger className="h-8 text-xs" data-testid="question-subject-code">
                <SelectValue placeholder="Select subject" />
              </SelectTrigger>
              <SelectContent>
                {courseSubjects.map(s => (
                  <SelectItem key={s.subject_code} value={s.subject_code}>
                    {s.subject_code} - {s.subject_title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={formData.subject_code}
              onChange={(e) => setFormData({...formData, subject_code: e.target.value.toUpperCase()})}
              placeholder="NOS-001"
              className="h-8 text-xs font-mono"
              data-testid="question-subject-code-input"
            />
          )}
        </div>
        <div>
          <Label className="text-xs text-blue-700">Subject Title</Label>
          <Input
            value={formData.subject_title}
            onChange={(e) => setFormData({...formData, subject_title: e.target.value})}
            placeholder="Subject name"
            className="h-8 text-xs"
            data-testid="question-subject-title"
          />
        </div>
        <div>
          <Label className="text-xs text-blue-700">Subject Type</Label>
          <Select 
            value={formData.subject_type || 'Core'}
            onValueChange={(v) => setFormData({...formData, subject_type: v})}
          >
            <SelectTrigger className="h-8 text-xs" data-testid="question-subject-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Core">Core</SelectItem>
              <SelectItem value="Non-Core">Non-Core</SelectItem>
              <SelectItem value="Elective">Elective</SelectItem>
              <SelectItem value="Practical">Practical</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {courseSubjects.length === 0 && formData.course_id && (
          <p className="col-span-3 text-xs text-blue-600">
            Course mein subjects add nahi hain. Admin &gt; Courses mein ja kar subjects add karein, ya manually likhen.
          </p>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Difficulty</Label>
          <Select 
            value={formData.difficulty} 
            onValueChange={(v) => setFormData({...formData, difficulty: v})}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DIFFICULTY_LEVELS.map(d => (
                <SelectItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Question Type</Label>
          <Select 
            value={formData.question_type} 
            onValueChange={(v) => setFormData({...formData, question_type: v})}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {QUESTION_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Correct Answer</Label>
          <Select 
            value={formData.correct_answer} 
            onValueChange={(v) => setFormData({...formData, correct_answer: v})}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="A">A</SelectItem>
              <SelectItem value="B">B</SelectItem>
              <SelectItem value="C">C</SelectItem>
              <SelectItem value="D">D</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
          <Languages className="h-4 w-4" /> English Version
        </h4>
        <div className="space-y-3">
          <div>
            <Label>Question (English) *</Label>
            <Textarea
              value={formData.question_en}
              onChange={(e) => setFormData({...formData, question_en: e.target.value})}
              placeholder="Enter question in English..."
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Option A (English)</Label>
              <Input
                value={formData.option_a_en}
                onChange={(e) => setFormData({...formData, option_a_en: e.target.value})}
              />
            </div>
            <div>
              <Label>Option B (English)</Label>
              <Input
                value={formData.option_b_en}
                onChange={(e) => setFormData({...formData, option_b_en: e.target.value})}
              />
            </div>
            <div>
              <Label>Option C (English)</Label>
              <Input
                value={formData.option_c_en}
                onChange={(e) => setFormData({...formData, option_c_en: e.target.value})}
              />
            </div>
            <div>
              <Label>Option D (English)</Label>
              <Input
                value={formData.option_d_en}
                onChange={(e) => setFormData({...formData, option_d_en: e.target.value})}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-slate-800 flex items-center gap-2">
            <Languages className="h-4 w-4" /> Hindi Version (हिंदी)
          </h4>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAutoTranslate}
            disabled={translating || !formData.question_en}
            className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200 hover:border-purple-400"
          >
            {translating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Translating...
              </>
            ) : (
              <>
                <Wand2 className="h-4 w-4 mr-2 text-purple-500" /> Auto-Translate with AI
              </>
            )}
          </Button>
        </div>
        <div className="space-y-3">
          <div>
            <Label>Question (Hindi) *</Label>
            <Textarea
              value={formData.question_hi}
              onChange={(e) => setFormData({...formData, question_hi: e.target.value})}
              placeholder="प्रश्न हिंदी में लिखें..."
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Option A (Hindi)</Label>
              <Input
                value={formData.option_a_hi}
                onChange={(e) => setFormData({...formData, option_a_hi: e.target.value})}
              />
            </div>
            <div>
              <Label>Option B (Hindi)</Label>
              <Input
                value={formData.option_b_hi}
                onChange={(e) => setFormData({...formData, option_b_hi: e.target.value})}
              />
            </div>
            <div>
              <Label>Option C (Hindi)</Label>
              <Input
                value={formData.option_c_hi}
                onChange={(e) => setFormData({...formData, option_c_hi: e.target.value})}
              />
            </div>
            <div>
              <Label>Option D (Hindi)</Label>
              <Input
                value={formData.option_d_hi}
                onChange={(e) => setFormData({...formData, option_d_hi: e.target.value})}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BookOpen className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-slate-800">Question Bank Management</h1>
                <p className="text-sm text-slate-500">Manage bilingual questions for all professional courses</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" /> Overview
            </TabsTrigger>
            <TabsTrigger value="manage" className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" /> Manage Questions
            </TabsTrigger>
            <TabsTrigger value="bulk-upload" className="flex items-center gap-2">
              <CloudUpload className="h-4 w-4" /> Bulk Upload
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            {renderOverview()}
          </TabsContent>

          <TabsContent value="manage">
            {renderManage()}
          </TabsContent>

          <TabsContent value="bulk-upload">
            {renderBulkUpload()}
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Question Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-blue-500" /> Add New Question
            </DialogTitle>
          </DialogHeader>
          <QuestionForm />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddQuestion} disabled={loading}>
              {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Question
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Question Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-amber-500" /> Edit Question
            </DialogTitle>
          </DialogHeader>
          <QuestionForm isEdit />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEditQuestion} disabled={loading}>
              {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Update Question
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" /> Delete Question
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Are you sure you want to delete this question? This action cannot be undone.
          </p>
          {editingQuestion && (
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <p className="font-medium">{editingQuestion.question_en}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteQuestion} disabled={loading}>
              {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Eye className="h-5 w-5" /> Question Preview
              </span>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant={previewLang === 'en' ? 'default' : 'outline'}
                  onClick={() => setPreviewLang('en')}
                >
                  English
                </Button>
                <Button 
                  size="sm" 
                  variant={previewLang === 'hi' ? 'default' : 'outline'}
                  onClick={() => setPreviewLang('hi')}
                >
                  हिंदी
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          {previewQuestion && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="font-medium text-slate-800 text-lg">
                  {previewLang === 'en' ? previewQuestion.question_en : previewQuestion.question_hi}
                </p>
              </div>
              <div className="space-y-2">
                {['A', 'B', 'C', 'D'].map(opt => {
                  const isCorrect = previewQuestion.correct_answer === opt;
                  const optionKey = `option_${opt.toLowerCase()}_${previewLang}`;
                  return (
                    <div 
                      key={opt}
                      className={`p-3 rounded-lg border-2 flex items-center gap-3 ${
                        isCorrect 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-slate-200'
                      }`}
                    >
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        isCorrect ? 'bg-green-500 text-white' : 'bg-slate-100'
                      }`}>
                        {opt}
                      </span>
                      <span className="flex-1">
                        {previewQuestion[optionKey]}
                      </span>
                      {isCorrect && <CheckCircle className="h-5 w-5 text-green-500" />}
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2 text-xs">
                <span className="px-2 py-1 bg-slate-100 rounded">{previewQuestion.module}</span>
                <span className={`px-2 py-1 rounded ${
                  previewQuestion.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                  previewQuestion.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                  'bg-amber-100 text-amber-700'
                }`}>
                  {previewQuestion.difficulty}
                </span>
                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                  {previewQuestion.question_type}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminQuestionBankPage;
